import { api, apiList } from '../../api/client';

export type AssetPurpose = 'document' | 'vehicle_photo' | 'portfolio';
export type DocumentType = 'identity' | 'selfie' | 'driver_license' | 'vehicle_registration' | 'license' | 'company';

export interface FileAsset {
  id: number;
  original_filename: string;
  purpose: AssetPurpose;
  url: string;
  visibility: 'private' | 'public';
}

export interface CarrierDocument {
  id: number;
  document_type: DocumentType;
  file: number;
  file_url?: string;
  original_filename?: string;
  content_type?: string;
  media_kind?: string;
  status: 'draft' | 'pending' | 'approved' | 'rejected';
  title: string;
}

export interface CarrierVehicle {
  body_type: string;
  id: number;
  make_model: string;
  pet_features: string;
  plate_number: string;
  year: number | null;
}

export interface CarrierProfile {
  id: number;
  carrier_type: 'person' | 'company';
  service_type: 'auto' | 'air_courier' | 'cargo_broker' | 'airport_meet';
  verification_status: 'draft' | 'pending' | 'needs_changes' | 'approved' | 'rejected' | 'expired';
  vehicles: CarrierVehicle[];
  vehicle_photos: FileAsset[];
  portfolio_photos: FileAsset[];
}

export interface VerificationStatus {
  id: number;
  verification_status: CarrierProfile['verification_status'];
  verification_rating_percent: number;
  can_offer: boolean;
}

export interface CarrierVehiclePatch {
  vehicle_body_type: string;
  vehicle_make_model: string;
  vehicle_pet_features: string;
  vehicle_plate_number: string;
  vehicle_year: number | null;
}

export function getCarrierProfile(): Promise<CarrierProfile> {
  return api<CarrierProfile>('/api/profiles/me/carrier/');
}

export function updateCarrierVehicle(payload: CarrierVehiclePatch): Promise<CarrierProfile> {
  return api<CarrierProfile>('/api/profiles/me/carrier/', { method: 'PATCH', body: { ...payload } });
}

export function getCarrierDocuments(): Promise<CarrierDocument[]> {
  return apiList<CarrierDocument>('/api/verification/documents/');
}

import { optimizeImageForUpload } from '../../utils/imageCompressor';

export async function uploadAsset(
  file: File,
  purpose: AssetPurpose,
  visibility: 'private' | 'public',
): Promise<FileAsset> {
  const optimized = await optimizeImageForUpload(file);
  const body = new FormData();
  body.append('file', optimized);
  body.append('purpose', purpose);
  body.append('visibility', visibility);
  return api<FileAsset>('/api/media/', { method: 'POST', body });
}

export function attachCarrierDocument(
  documentType: DocumentType,
  file: number,
  title = '',
): Promise<CarrierDocument> {
  return api<CarrierDocument>('/api/verification/documents/', {
    method: 'POST',
    body: { document_type: documentType, file, title },
  });
}

export function deleteAsset(id: number): Promise<void> {
  return api<void>(`/api/media/${id}/`, { method: 'DELETE' });
}

export function submitCarrierVerification(): Promise<VerificationStatus> {
  return api<VerificationStatus>('/api/verification/submit/', { method: 'POST' });
}

export function getCarrierVerificationStatus(): Promise<VerificationStatus> {
  return api<VerificationStatus>('/api/verification/status/');
}
